import React, { useState } from 'react';

// ---------------------------------------------------------------------------
// Denial Resolve v2 result view.
//
// Renders the richer denial_resolve_v2 outputs (jargon-free resolution summary,
// 13-action plan, family specialist finding, the SME Resolution Cortex tactics
// that fired, appeal packet, confidence/HITL decision, payer submission and the
// cited-source audit trail) and the interrupt-for-input form the minimal input
// contract preflight raises when required fields are missing.
//
// It reads master node-results by their v2 node ids and degrades gracefully:
// any section with no data renders a small empty hint instead of breaking.
// ---------------------------------------------------------------------------

const DASH = '-';

function pickOutputs(res) {
  if (!res) return {};
  return res.outputs_produced || res.outputs || res.output || res.result || {};
}

function fields(nodeResults, id) {
  return pickOutputs(nodeResults?.[id]);
}

function asObj(v) {
  if (!v) return {};
  if (Array.isArray(v)) return v[0] || {};
  if (typeof v === 'object') return v;
  return {};
}

// Single source of truth for appealability in the UI. Prefers the deterministic
// appealability_gate node; falls back to the enforced not_appealable block on the
// packet. Defaults to "appealable" so a missing gate never hides a valid letter.
function appealability(nodeResults) {
  const gate = fields(nodeResults, 'appealability_gate');
  const aa = fields(nodeResults, 'attachment_assembler');
  const pkt = asObj(aa.appeal_packet || fields(nodeResults, 'resolution_packet').appeal_packet);
  const na = asObj(pkt.not_appealable);
  let isAppealable;
  if (gate.is_appealable != null) isAppealable = gate.is_appealable === true;
  else if (na.is_appealable === false) isAppealable = false;
  else isAppealable = true;
  const reasons = (Array.isArray(gate.not_appealable_reasons) && gate.not_appealable_reasons.length)
    ? gate.not_appealable_reasons
    : (Array.isArray(na.reasons) ? na.reasons : []);
  const message = gate.not_appealable_message || na.message || '';
  const isCorrectedClaim = gate.is_corrected_claim === true;
  return { isAppealable, isCorrectedClaim, reasons, message };
}

function formatCurrency(v) {
  if (v === null || v === undefined || v === '') return DASH;
  const n = Number(v);
  if (Number.isNaN(n)) return DASH;
  return n.toLocaleString(undefined, {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

function humanAction(action) {
  if (!action) return '';
  return {
    file_appeal: 'file an appeal',
    prepare_appeal_draft: 'prepare appeal draft / verify authorization',
    submit_corrected_claim: 'submit a corrected claim',
    documentation_submission: 'submit documentation',
    payer_follow_up: 'follow up with the payer',
    cob_resubmission: 'resubmit with COB proof',
    auth_correction: 'correct the authorization',
    coding_review: 'send for coding review',
    clinical_review: 'send for clinical review',
    underpayment_dispute: 'dispute the underpayment',
    write_off: 'write off',
    patient_responsibility: 'bill patient responsibility',
    frontend_rejection_repair: 'repair and resubmit the claim',
    human_review: 'send to human review',
    appeal_draft: 'prepare appeal draft',
    corrected_claim_draft: 'prepare corrected-claim draft',
    documentation_draft: 'prepare documentation packet (draft)',
    draft_pending_human_review: 'prepare draft for human review',
    payer_follow_up_task: 'follow up with the payer (task)',
    coding_review_task: 'send for coding review (task)',
    clinical_review_task: 'send for clinical review (task)',
    human_review_task: 'send to human review (task)',
  }[action] || action.replace(/_/g, ' ');
}

function confTone(pct) {
  if (pct == null) return null;
  const n = Number(pct);
  if (n >= 80) return 'good';
  if (n >= 50) return 'warn';
  return 'bad';
}

function Subsection({ idx, title, hint, children }) {
  return (
    <section className="denials-subsection">
      <h3 className="denials-subsection__title">
        <span className="denials-subsection__idx">{idx}</span>{title}
      </h3>
      {hint && <p className="denials-subsection__hint">{hint}</p>}
      <div className="denials-subsection__body">{children}</div>
    </section>
  );
}

function Empty({ children }) {
  return <div className="denials-subsection__empty">{children || 'No data for this section yet.'}</div>;
}

function Chip({ label, value, tone }) {
  if (value == null || value === '' || value === DASH) return null;
  return (
    <div className={`denials-chip ${tone ? `denials-chip--${tone}` : ''}`}>
      <span className="denials-chip__label">{label}</span>
      <span className="denials-chip__value">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Interrupt-for-input: the minimal input contract preflight asked for fields.
// ---------------------------------------------------------------------------

function InputRequestForm({ request, onSubmit, submitting }) {
  const reqFields = Array.isArray(request?.fields) ? request.fields : [];
  const [values, setValues] = useState({});

  const update = (id, v) => setValues((s) => ({ ...s, [id]: v }));
  const allHardFilled = reqFields
    .filter((f) => f.required === 'hard')
    .every((f) => String(values[f.id] ?? '').trim() !== '');

  return (
    <div className="denials-processing-card denials-processing-card--error" style={{ textAlign: 'left' }}>
      <div className="denials-processing-card__title">We need a few details to continue</div>
      <p className="denials-processing-card__text">
        {request?.message || 'Before resolving this denial we need the following information.'}
      </p>
      <div style={{ display: 'grid', gap: 12, marginTop: 12, maxWidth: 560 }}>
        {reqFields.map((f) => (
          <label key={f.id} className="denials-field" style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontWeight: 600 }}>
              {f.label}{f.required === 'hard' ? ' *' : ' (optional)'}
            </span>
            {f.why && <span style={{ fontSize: 12, color: '#6b7280' }}>{f.why}</span>}
            <input
              type={f.type === 'date' ? 'date' : 'text'}
              value={values[f.id] ?? ''}
              onChange={(e) => update(f.id, e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
            />
          </label>
        ))}
      </div>
      <div style={{ marginTop: 14 }}>
        <button
          className="denials-btn denials-btn--primary"
          onClick={() => onSubmit(values)}
          disabled={submitting || !allHardFilled}
        >
          {submitting ? 'Resuming...' : 'Provide details & continue'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result sections
// ---------------------------------------------------------------------------

function NotAppealableBanner({ nodeResults }) {
  // Hidden once the new DecisionSummaryBanner is rendering the canonical
  // triad; we keep this banner only as a fallback when the consolidator
  // could not produce a decision (rare). It also renders missing evidence
  // with a clear stem rather than the legacy "missing X present" phrasing.
  const consolidator = fields(nodeResults, 'final_consolidator');
  const rec = asObj(consolidator.recommendation);
  if (rec && rec.decision) return null;

  const { isAppealable, message, reasons } = appealability(nodeResults);
  if (isAppealable) return null;
  const gate = fields(nodeResults, 'appealability_gate');
  const missingEvidence = Array.isArray(gate.not_appealable_missing_evidence)
    ? gate.not_appealable_missing_evidence
    : [];
  const text = message
    || 'An appeal is not ready on the submitted records. Resolve the gaps below, then re-run.';
  return (
    <div className="denials-alert denials-alert--warn" style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>Appeal not ready</div>
      <p style={{ margin: 0 }}>{text}</p>
      {reasons.length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
          {reasons.map((r, i) => <li key={i}>{String(r)}</li>)}
        </ul>
      )}
      {missingEvidence.length > 0 && (
        <>
          <div style={{ marginTop: 10, fontWeight: 600 }}>
            Evidence not yet provided (the submitted records do not show)
          </div>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {missingEvidence.map((m, i) => <li key={i}>{String(m)}</li>)}
          </ul>
        </>
      )}
    </div>
  );
}

// Canonical decision triad from the final_consolidator. Renders at the TOP
// of the v2 view so the user sees one coherent answer (decision +
// appealability + recommended next action) before any advisory section.
function DecisionSummaryBanner({ nodeResults }) {
  const consolidator = fields(nodeResults, 'final_consolidator');
  const rec = asObj(consolidator.recommendation);
  if (!rec || !rec.decision) return null;
  const next = asObj(rec.recommended_next_action);
  const status = (rec.appealability_status || '').toLowerCase();
  const evidenceNeeded = Array.isArray(rec.evidence_needed_from_user)
    ? rec.evidence_needed_from_user
    : [];
  const missingToImprove = Array.isArray(rec.missing_to_improve)
    ? rec.missing_to_improve
    : [];
  const crossPayer = Array.isArray(rec.cross_payer_citation_notice)
    ? rec.cross_payer_citation_notice
    : [];
  const tone = status === 'ready'
    ? 'denials-chip--good'
    : status === 'conditional'
      ? 'denials-chip--warn'
      : status === 'not_ready'
        ? 'denials-chip--warn'
        : 'denials-chip--neutral';
  const statusLabel = status === 'ready'
    ? 'Appeal ready'
    : status === 'conditional'
      ? 'Conditionally appealable - human review required'
      : status === 'not_ready'
        ? 'Appeal not ready'
        : status === 'n/a'
          ? 'Appeal not applicable'
          : 'Appeal status pending';
  const expected = Number(rec.expected_recovery || 0);
  return (
    <div className="denials-alert" style={{
      marginBottom: 16,
      background: '#f8fafc',
      border: '1px solid #cbd5e1',
      borderLeft: '4px solid #0f172a',
    }}>
      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{rec.decision}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        <span className={`denials-chip ${tone}`}>
          <span className="denials-chip__label">Appealability</span>
          <span className="denials-chip__value">{statusLabel}</span>
        </span>
        {next.headline && (
          <span className="denials-chip">
            <span className="denials-chip__label">Recommended next action</span>
            <span className="denials-chip__value">{next.headline}</span>
          </span>
        )}
        {rec.confidence_pct != null && (
          <span className={`denials-chip ${
            rec.confidence_band === 'high' ? 'denials-chip--good'
              : rec.confidence_band === 'medium' ? 'denials-chip--warn'
                : 'denials-chip--neutral'
          }`}>
            <span className="denials-chip__label">Confidence</span>
            <span className="denials-chip__value">{`${Number(rec.confidence_pct).toFixed(0)}%`}</span>
          </span>
        )}
        <span className="denials-chip">
          <span className="denials-chip__label">Expected recovery</span>
          <span className="denials-chip__value">
            {status === 'ready'
              ? expected > 0 ? `$${expected.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'TBD'
              : 'Low unless authorization / exception evidence is found'}
          </span>
        </span>
      </div>
      {(() => {
        const us = (rec && typeof rec === 'object' && rec.user_summary && typeof rec.user_summary === 'object')
          ? rec.user_summary : null;
        if (us && (us.reason || us.next_step)) {
          return (
            <div style={{ marginBottom: 8, lineHeight: 1.5 }}>
              {us.reason && (
                <p style={{ margin: '0 0 6px' }}>
                  <strong>Reason: </strong>{us.reason}
                </p>
              )}
              {us.next_step && (
                <p style={{ margin: 0 }}>
                  <strong>Next step: </strong>{us.next_step}
                </p>
              )}
            </div>
          );
        }
        if (rec && rec.rationale) {
          return <p style={{ margin: '0 0 8px', lineHeight: 1.5 }}>{rec.rationale}</p>;
        }
        return null;
      })()}
      {evidenceNeeded.length > 0 && (
        <>
          <div style={{ fontWeight: 600, marginTop: 6 }}>What we need from you</div>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {evidenceNeeded.map((e, i) => <li key={i}>{String(e)}</li>)}
          </ul>
        </>
      )}
      {evidenceNeeded.length === 0 && missingToImprove.length > 0 && (
        <>
          <div style={{ fontWeight: 600, marginTop: 6 }}>
            Evidence that would improve this resolution (the submitted records do not yet show)
          </div>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {missingToImprove.slice(0, 8).map((m, i) => <li key={i}>{String(m)}</li>)}
          </ul>
        </>
      )}
      {crossPayer.length > 0 && (
        <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b' }}>
          Note: {crossPayer.join(', ')} {crossPayer.length === 1 ? 'was' : 'were'} used as cross-payer reference{crossPayer.length === 1 ? '' : 's'}; verify this payer&apos;s own rule before acting.
        </p>
      )}
    </div>
  );
}

function CaseOverviewSection({ nodeResults }) {
  const pkt = fields(nodeResults, 'resolution_packet');
  const rs = asObj(pkt.resolution_summary);
  // Build a flowing narrative from the plain-language summary fields.
  const parts = [rs.what_happened, rs.why_denied, rs.what_we_are_doing, rs.next_step]
    .map((s) => (s || '').trim())
    .filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <div className="denials-case-overview" style={{
      background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
      padding: '14px 16px', marginBottom: 16,
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>Case overview</div>
      <p style={{ margin: 0, lineHeight: 1.6 }}>{parts.join(' ')}</p>
    </div>
  );
}

function ResolutionSummarySection({ nodeResults }) {
  const pkt = fields(nodeResults, 'resolution_packet');
  const rs = asObj(pkt.resolution_summary);
  // QA round 2: prefer the deterministic user_summary from final_consolidator
  // so the summary table never contradicts the top-of-page Decision banner.
  // The LLM-authored fields fill the remaining cells (what_happened, etc.).
  const consolidator = fields(nodeResults, 'final_consolidator');
  const recObj = asObj(consolidator.recommendation);
  const us = asObj(recObj.user_summary);
  const evidenceNeeded = Array.isArray(recObj.evidence_needed_from_user)
    ? recObj.evidence_needed_from_user : [];
  const merged = {
    what_happened: rs.what_happened,
    why_denied: us.reason || rs.why_denied,
    what_we_are_doing: us.recommended_action || rs.what_we_are_doing,
    expected_recovery: us.expected_recovery || rs.expected_recovery,
    what_we_need_from_you: evidenceNeeded.length > 0
      ? evidenceNeeded
      : rs.what_we_need_from_you,
    confidence: us.appealability
      ? `Appealability: ${us.appealability}`
      : rs.confidence,
    next_step: us.next_step || rs.next_step,
  };
  const has = Object.values(merged).some((v) => v && (typeof v === 'string' ? v.trim() : (Array.isArray(v) ? v.length > 0 : true)));

  const renderWhatWeNeed = (val) => {
    if (Array.isArray(val)) {
      return (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {val.map((v, i) => <li key={i}>{String(v)}</li>)}
        </ul>
      );
    }
    return String(val);
  };

  return (
    <Subsection idx="1" title="Resolution Summary"
      hint="Plain-language explanation, free of codes and jargon.">
      {!has ? <Empty>The engine has not produced a plain-language summary yet.</Empty> : (
        <table className="denials-kv-table">
          <tbody>
            {merged.what_happened && <tr><th>What happened</th><td>{merged.what_happened}</td></tr>}
            {merged.why_denied && <tr><th>Why it was denied</th><td>{merged.why_denied}</td></tr>}
            {merged.what_we_are_doing && <tr><th>What we are doing</th><td>{merged.what_we_are_doing}</td></tr>}
            {merged.expected_recovery && <tr><th>Expected recovery</th><td>{merged.expected_recovery}</td></tr>}
            {merged.what_we_need_from_you && (
              <tr><th>What we need from you</th><td>{renderWhatWeNeed(merged.what_we_need_from_you)}</td></tr>
            )}
            {merged.confidence && <tr><th>Confidence</th><td>{merged.confidence}</td></tr>}
            {merged.next_step && <tr><th>Next step</th><td>{merged.next_step}</td></tr>}
          </tbody>
        </table>
      )}
    </Subsection>
  );
}

function ClassificationDecisionSection({ nodeResults }) {
  const cls = fields(nodeResults, 'denial_type_classifier');
  const rd = fields(nodeResults, 'review_decision');
  const cp = fields(nodeResults, 'correction_planner');
  const fc = fields(nodeResults, 'final_consolidator');
  const rec = (fc && typeof fc.recommendation === 'object' && fc.recommendation) || {};
  const appealStatus = rec.appealability_status || null;
  const nextAction = (rec.recommended_next_action && typeof rec.recommended_next_action === 'object')
    ? rec.recommended_next_action : {};
  const humanReview = rec.human_review_required === true
    || appealStatus === 'conditional' || appealStatus === 'not_ready';

  // Decision triad is canonical. Fall back to upstream review_decision only
  // when the consolidator did not produce a decision (legacy executions).
  let actionRaw = nextAction.action || rec.action || cp.overall_action || null;
  const submissionRaw = rec.submission_kind_override
    || nextAction.submission || cp.recommended_submission || null;
  // Conditional status = appealable-but-needs-human-verify => relabel "file
  // appeal" actions as "prepare draft". For not_ready (no appeal yet, just
  // follow up / send for review), keep the upstream action verb intact so
  // the chip and the banner agree.
  if (appealStatus === 'conditional' && actionRaw === 'file_appeal') {
    actionRaw = 'prepare_appeal_draft';
  } else if (appealStatus === 'conditional' && (actionRaw === 'submit_corrected_claim'
    || actionRaw === 'auth_correction'
    || actionRaw === 'cob_resubmission'
    || actionRaw === 'frontend_rejection_repair')) {
    actionRaw = 'corrected_claim_draft';
  } else if (appealStatus === 'conditional' && actionRaw === 'documentation_submission') {
    actionRaw = 'documentation_draft';
  } else if (appealStatus === 'conditional' && actionRaw === 'underpayment_dispute') {
    actionRaw = 'appeal_draft';
  }
  const confPct = (rec.confidence_pct != null) ? Number(rec.confidence_pct) : rd.final_confidence_pct;
  const conf = (confPct != null && !Number.isNaN(Number(confPct)))
    ? `${Number(confPct).toFixed(0)}%` : null;

  let decision;
  let decisionTone;
  if (humanReview) {
    decision = 'Needs human review';
    decisionTone = 'warn';
  } else if (rec.decision === 'auto_resolvable' || rec.auto_submit_eligible === true) {
    decision = 'Auto-approved';
    decisionTone = 'good';
  } else if (rec.decision === 'not_appealable' || appealStatus === 'not_appealable') {
    decision = 'Not appealable';
    decisionTone = 'warn';
  } else if (rd.auto_approved === true) {
    decision = 'Auto-approved';
    decisionTone = 'good';
  } else if (rd.human_review_required === true) {
    decision = 'Needs human review';
    decisionTone = 'warn';
  } else {
    decision = null;
    decisionTone = undefined;
  }

  // Why-this-decision: prefer the consolidator's rationale (it incorporates
  // the conditional gate, payer-primacy ranking, and not-appealable reasons).
  // Fall back to the upstream LLM decision_reason if the consolidator didn't run.
  const whyText = (rec.rationale || rd.decision_reason || '').trim();

  return (
    <Subsection idx="2" title="Classification & Decision">
      <div className="denials-summary-chips">
        <Chip label="Family" value={cls.family_name || cls.denial_family} />
        <Chip label="Subtype" value={cls.denial_subtype} />
        <Chip label="Action" value={actionRaw ? humanAction(actionRaw) : null} />
        <Chip label="Confidence" value={conf} tone={confTone(confPct)} />
        <Chip label="Decision" value={decision} tone={decisionTone} />
        <Chip label="Submission" value={submissionRaw} />
        {humanReview ? (
          <Chip label="Auto-approved" value="false" tone="warn" />
        ) : null}
      </div>
      {whyText && (
        <p className="denials-subsection__hint" style={{ marginTop: 8 }}>
          <strong>Why this decision:</strong> {whyText}
        </p>
      )}
    </Subsection>
  );
}

function CortexTacticsSection({ nodeResults }) {
  const cr = fields(nodeResults, 'capability_resolver');
  const caps = Array.isArray(cr.activated_capabilities) ? cr.activated_capabilities : [];
  const status = cr.cofire_resolver_status;
  const fired = cr.cofire_fired_count;
  return (
    <Subsection idx="3" title="Resolution Cortex (fired SME expertise)"
      hint="SME expertise-graph nodes that fired for THIS case, mapped to the capability packs that shaped the resolution. This is the human-like, case-customized reasoning layer.">
      {caps.length === 0 ? (
        <Empty>
          {status === 'no_fired_nodes'
            ? 'No expertise nodes fired for this case (the deterministic engine handled it on its own).'
            : status === 'no_bindings'
              ? `${fired || 0} expertise node(s) fired but none are bound to a capability pack yet.`
              : 'The Resolution Cortex did not run for this case (no expertise graph wired).'}
        </Empty>
      ) : (
        <table className="denials-coding-table">
          <thead>
            <tr><th>Tier</th><th>Capability</th><th>Why it fired</th><th>Fire score</th><th>Rank</th></tr>
          </thead>
          <tbody>
            {caps.map((c, i) => (
              <tr key={i}>
                <td><span className="denials-tag">{c.tier || '?'}</span></td>
                <td><strong>{c.title || c.capability_id}</strong>{c.side_effects ? ' (acts on payer)' : ''}</td>
                <td>{c.label || DASH}</td>
                <td className="denials-cell-num">{c.fire_score != null ? Number(c.fire_score).toFixed(2) : DASH}</td>
                <td className="denials-cell-num">{c.rank != null ? Number(c.rank).toFixed(2) : DASH}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Subsection>
  );
}

function SpecialistFindingSection({ nodeResults }) {
  const si = fields(nodeResults, 'specialist_invoke');
  const f = asObj(si.family_finding);
  const has = Object.keys(f).length > 0;
  const supported = Array.isArray(f.supported_issues) ? f.supported_issues : [];
  const unsupported = Array.isArray(f.unsupported_issues) ? f.unsupported_issues : [];
  const missing = Array.isArray(f.missing_data) ? f.missing_data : [];
  return (
    <Subsection idx="4" title="Specialist Finding">
      {!has ? <Empty>No family specialist finding for this case.</Empty> : (
        <div className="denials-narrative">
          {f.root_cause && <p><strong>Root cause:</strong> {f.root_cause}</p>}
          {f.recommended_action && <p><strong>Recommended action:</strong> {humanAction(f.recommended_action)}</p>}
          {f.summary && <p>{f.summary}</p>}
          {supported.length > 0 && (
            <>
              <h4>Supported issues</h4>
              <ul>{supported.map((s, i) => (
                <li key={i}>{typeof s === 'string' ? s : `${s.issue || ''}${s.evidence_citation ? ` -- ${s.evidence_citation}` : ''}`}</li>
              ))}</ul>
            </>
          )}
          {unsupported.length > 0 && (
            <>
              <h4>Payer reasons the engine found unsupported</h4>
              <ul>{unsupported.map((s, i) => (
                <li key={i}>{typeof s === 'string' ? s : `${s.issue || ''}${s.reason_unsupported ? ` -- ${s.reason_unsupported}` : ''}`}</li>
              ))}</ul>
            </>
          )}
          {missing.length > 0 && (
            <>
              <h4>Missing data</h4>
              <ul>{missing.map((s, i) => <li key={i}>{String(s)}</li>)}</ul>
            </>
          )}
        </div>
      )}
    </Subsection>
  );
}

function PlanSection({ nodeResults }) {
  const cp = fields(nodeResults, 'correction_planner');
  const plan = Array.isArray(cp.plan) ? cp.plan : [];
  if (plan.length === 0) {
    return <Subsection idx="5" title="Correction Plan"><Empty>No correction plan produced.</Empty></Subsection>;
  }
  return (
    <Subsection idx="5" title="Correction Plan (13-action policy)">
      <table className="denials-issues-table">
        <thead><tr><th>Issue</th><th>Action</th><th>Rationale</th><th>Cited sources</th></tr></thead>
        <tbody>
          {plan.map((p, i) => (
            <tr key={i}>
              <td>{p.issue || DASH}</td>
              <td><span className="denials-action-badge">{humanAction(p.action)}</span></td>
              <td>{p.rationale || DASH}</td>
              <td>{Array.isArray(p.cited_source_documents) ? p.cited_source_documents.join(', ') : DASH}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Subsection>
  );
}

function FinancialSection({ nodeResults }) {
  const fe = fields(nodeResults, 'financial_estimator');
  const up = asObj(fields(nodeResults, 'underpayment_recalc').underpayment);
  const { isAppealable } = appealability(nodeResults);
  const has = fe.expected_value != null || fe.gross_disputed != null || Object.keys(up).length > 0;
  if (!has) return <Subsection idx="6" title="Financial Impact"><Empty>No financial estimate yet.</Empty></Subsection>;
  return (
    <Subsection idx="6" title="Financial Impact">
      <table className="denials-kv-table">
        <tbody>
          <tr><th>Gross disputed</th><td>{formatCurrency(fe.gross_disputed)}</td></tr>
          <tr><th>Expected recovery</th><td><strong>{
            isAppealable ? formatCurrency(fe.expected_value) : '$0.00 - no recovery expected'
          }</strong></td></tr>
          <tr><th>Priority</th><td>{fe.priority || DASH}</td></tr>
          {isAppealable && fe.historical_win_rate != null && (
            <tr><th>Historical win rate</th><td>{`${(Number(fe.historical_win_rate) * 100).toFixed(0)}%`}</td></tr>
          )}
          {up.underpaid && <tr><th>Underpayment variance</th><td>{formatCurrency(up.variance)}</td></tr>}
        </tbody>
      </table>
    </Subsection>
  );
}

function AppealPacketSection({ nodeResults }) {
  const aa = fields(nodeResults, 'attachment_assembler');
  const pkt = asObj(aa.appeal_packet || fields(nodeResults, 'resolution_packet').appeal_packet);
  const { isAppealable, isCorrectedClaim, message } = appealability(nodeResults);
  // Never show an appeal letter unless the case is genuinely appealable.
  const letter = (isAppealable && !isCorrectedClaim) ? pkt.appeal_letter : '';
  const evidence = Array.isArray(pkt.evidence_table) ? pkt.evidence_table : [];
  const crosswalk = Array.isArray(pkt.policy_crosswalk) ? pkt.policy_crosswalk : [];
  const attachments = Array.isArray(aa.attachments) ? aa.attachments
    : (Array.isArray(pkt.attachments) ? pkt.attachments : []);
  const attachStatus = Array.isArray(aa.attachment_status) ? aa.attachment_status
    : (Array.isArray(pkt.attachment_status) ? pkt.attachment_status : []);
  const checklist = attachStatus.length > 0
    ? attachStatus
    : (Array.isArray(pkt.attachment_checklist) ? pkt.attachment_checklist : []);

  // Not appealable: show the clear recommendation instead of any letter.
  if (!isAppealable) {
    return (
      <Subsection idx="7" title="Recommendation">
        <div className="denials-alert denials-alert--warn">
          <p style={{ margin: 0 }}>{message
            || 'Based on the submitted data this case is not appealable. If you have supporting '
               + 'evidence, please submit the case again with the supporting evidence for reprocessing.'}</p>
        </div>
        {pkt.requested_resolution && (
          <p className="denials-subsection__hint" style={{ marginTop: 8 }}>
            <strong>Recommended action:</strong> {pkt.requested_resolution}
          </p>
        )}
      </Subsection>
    );
  }

  const title = isCorrectedClaim ? 'Corrected-Claim Packet' : 'Appeal-Ready Packet';
  const has = letter || evidence.length || crosswalk.length || checklist.length || attachments.length
    || pkt.requested_resolution;
  return (
    <Subsection idx="7" title={title}>
      {!has ? <Empty>No packet generated yet.</Empty> : (
        <>
          {letter && <div className="denials-appeal-letter">{letter}</div>}
          {pkt.requested_resolution && (
            <p className="denials-subsection__hint" style={{ marginTop: 8 }}>
              <strong>Requested resolution:</strong> {pkt.requested_resolution}
            </p>
          )}
          {evidence.length > 0 && (
            <table className="denials-evidence-table" style={{ marginTop: 12 }}>
              <thead><tr><th>Date</th><th>Evidence</th></tr></thead>
              <tbody>{evidence.map((r, i) => (
                <tr key={i}><td className="denials-cell-num">{r.date || DASH}</td><td>{r.evidence || DASH}</td></tr>
              ))}</tbody>
            </table>
          )}
          {crosswalk.length > 0 && (
            <table className="denials-crosswalk-table" style={{ marginTop: 12 }}>
              <thead><tr><th>Policy reference</th><th>Clause</th><th>Appeal argument</th></tr></thead>
              <tbody>{crosswalk.map((r, i) => (
                <tr key={i}>
                  <td><code>{r.policy_graph_ref || DASH}</code></td>
                  <td>{r.clause || DASH}</td>
                  <td>{r.appeal_argument_paragraph || DASH}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
          {attachments.length > 0 && (
            <>
              <h4 style={{ marginTop: 12 }}>Retrieved documents (attached)</h4>
              <table className="denials-evidence-table">
                <thead><tr><th>Type</th><th>Title</th><th>Date</th></tr></thead>
                <tbody>{attachments.map((a, i) => (
                  <tr key={i}>
                    <td>{a.type || DASH}</td>
                    <td>{a.title || DASH}</td>
                    <td className="denials-cell-num">{a.date || DASH}</td>
                  </tr>
                ))}</tbody>
              </table>
            </>
          )}
          {checklist.length > 0 && (
            <>
              <h4 style={{ marginTop: 12 }}>Attachment checklist</h4>
              <ul className="denials-doc-list">
                {checklist.map((c, i) => {
                  if (typeof c === 'string') return <li key={i}>{c}</li>;
                  if (c && c.item) {
                    const ok = c.status === 'retrieved' || c.satisfied;
                    return (
                      <li key={i}>
                        <span className={`denials-tag ${ok ? '' : 'denials-tag--warn'}`}>
                          {ok ? 'retrieved' : 'needed'}
                        </span>{' '}{c.item}
                      </li>
                    );
                  }
                  return <li key={i}>{c.label || JSON.stringify(c)}</li>;
                })}
              </ul>
            </>
          )}
        </>
      )}
    </Subsection>
  );
}

function ClinicalEvidenceSection({ nodeResults }) {
  const tb = fields(nodeResults, 'clinical_timeline_builder');
  const ehr = fields(nodeResults, 'evidence_fetch_ehr');
  const timeline = asObj(tb.evidence_timeline);
  const prose = tb.prioritized_evidence_bundle;
  const dates = Object.keys(timeline).filter((k) => timeline[k] != null).sort();

  // QA round 2: surface the user-supplied clinical note alongside any EHR
  // data so this section never contradicts the top banner that already
  // acknowledges the note. clinical_documentation_excerpt is set by the
  // final_consolidator when preflight captured a clinical note from the
  // interrupt-and-resume flow.
  const consolidator = fields(nodeResults, 'final_consolidator');
  const recObj = asObj(consolidator.recommendation);
  const clinicalNote = String(recObj.clinical_documentation_excerpt || '').trim();

  // Fallback: flatten the raw EHR bundle into a simple type/items list.
  const bundle = asObj(ehr.evidence_bundle);
  const bundleData = asObj(bundle.data && typeof bundle.data === 'object' ? bundle.data : bundle);
  const bundleRows = [];
  Object.keys(bundleData).forEach((k) => {
    const v = bundleData[k];
    if (Array.isArray(v) && v.length) bundleRows.push([k, v]);
  });

  const renderEntry = (entry) => {
    if (entry == null) return DASH;
    if (typeof entry === 'string') return entry;
    if (Array.isArray(entry)) {
      return (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {entry.map((e, i) => (
            <li key={i}>{typeof e === 'string' ? e : (e.title || e.body || e.text || JSON.stringify(e))}</li>
          ))}
        </ul>
      );
    }
    if (typeof entry === 'object') {
      return (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {Object.keys(entry).map((k) => (
            <li key={k}><strong>{k.replace(/_/g, ' ')}:</strong>{' '}
              {typeof entry[k] === 'object' ? JSON.stringify(entry[k]) : String(entry[k])}</li>
          ))}
        </ul>
      );
    }
    return String(entry);
  };

  const hasTimeline = dates.length > 0;
  const hasBundle = bundleRows.length > 0;
  const hasNote = clinicalNote.length > 0;
  const has = hasTimeline || hasBundle || hasNote || (prose && String(prose).trim());

  return (
    <Subsection idx="7b" title="Clinical Evidence & Notes"
      hint="Clinical documentation captured for this case (user-supplied notes, EHR records, labs, vitals) used to build the resolution.">
      {!has ? (
        <Empty>No clinical records were captured for this patient and date range. Add clinical notes on the case and resubmit so the engine can use them.</Empty>
      ) : (
        <>
          {hasNote && (
            <div style={{
              background: '#f1f5f9',
              border: '1px solid #cbd5e1',
              borderLeft: '4px solid #0ea5e9',
              padding: '10px 14px',
              borderRadius: 6,
              marginBottom: 12,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                User-supplied clinical note
              </div>
              <div style={{ fontSize: 12, color: '#475569', marginBottom: 6 }}>
                Provided at the input prompt and merged into the case. Supports
                medical necessity; for authorization-family denials, medical
                necessity alone does not overturn the denial unless a payer
                exception applies.
              </div>
              <pre style={{
                whiteSpace: 'pre-wrap',
                margin: 0,
                fontFamily: 'inherit',
                fontSize: 13,
                lineHeight: 1.5,
                background: 'transparent',
              }}>{clinicalNote}</pre>
            </div>
          )}
          {prose && String(prose).trim() && (
            <p className="denials-narrative" style={{ marginBottom: 12 }}>{prose}</p>
          )}
          {hasTimeline && (
            <table className="denials-evidence-table">
              <thead><tr><th>Date</th><th>Evidence</th></tr></thead>
              <tbody>{dates.map((d) => (
                <tr key={d}><td className="denials-cell-num">{d}</td><td>{renderEntry(timeline[d])}</td></tr>
              ))}</tbody>
            </table>
          )}
          {!hasTimeline && hasBundle && (
            <table className="denials-evidence-table">
              <thead><tr><th>Type</th><th>Records</th></tr></thead>
              <tbody>{bundleRows.map(([k, v]) => (
                <tr key={k}><td>{k.replace(/_/g, ' ')}</td><td>{renderEntry(v)}</td></tr>
              ))}</tbody>
            </table>
          )}
          {!hasTimeline && !hasBundle && hasNote && (
            <p className="denials-subsection__hint" style={{ marginTop: 4 }}>
              EHR fetch returned no progress notes, vitals, or labs for this
              date range; the user-supplied clinical note above is the
              clinical evidence on file.
            </p>
          )}
        </>
      )}
    </Subsection>
  );
}

function SubmissionAuditSection({ nodeResults }) {
  const sub = fields(nodeResults, 'submit_payer');
  const uhc = fields(nodeResults, 'uhc_payload_builder');
  const persist = fields(nodeResults, 'persist_outcome');
  const audit = asObj(fields(nodeResults, 'audit_trail_emit').audit_trail);
  const fc = fields(nodeResults, 'final_consolidator');
  const rec = (fc && typeof fc.recommendation === 'object' && fc.recommendation) || {};
  const appealStatus = rec.appealability_status || null;
  const humanReview = rec.human_review_required === true
    || appealStatus === 'conditional' || appealStatus === 'not_ready';
  const resp = sub.response;
  const respObj = asObj(resp);
  const cited = Array.isArray(audit.cited_source_documents) ? audit.cited_source_documents : [];

  // Source-of-truth pivot: when the consolidator says human-review-required,
  // every audit/submission chip MUST agree with the top decision banner.
  // Otherwise fall through to the upstream submit/audit node outputs.
  const submissionKind = humanReview
    ? (rec.submission_kind_override || 'appeal_draft')
    : uhc.submission_kind;
  const autoApproved = humanReview ? false : (audit.auto_approved === true);
  const filedAuto = humanReview ? false : (uhc.filed_automatically === true);
  const autoDisabled = humanReview || respObj.status === 'autosubmit_disabled';
  const savedValue = humanReview
    ? (persist.appeal_status || 'pending_review')
    : (persist.appeal_status || (persist.persisted ? 'saved' : null));

  let disposition;
  if (humanReview) {
    if (submissionKind === 'corrected_claim_draft') {
      disposition = 'Corrected-claim draft prepared. Human review required before payer submission.';
    } else if (submissionKind === 'documentation_draft') {
      disposition = 'Documentation packet prepared (draft). Human review required before payer submission.';
    } else if (submissionKind === 'appeal_draft' || submissionKind === 'draft_pending_human_review') {
      disposition = 'Appeal draft prepared. Human review required before payer submission.';
    } else if (submissionKind === 'payer_follow_up_task') {
      disposition = 'Payer follow-up required before resubmission. No claim was filed; the case is held for the workqueue.';
    } else if (submissionKind === 'coding_review_task' || submissionKind === 'clinical_review_task') {
      disposition = 'Routed for internal review (no payer submission). Held in pending_review.';
    } else if (appealStatus === 'not_ready') {
      disposition = 'Not appealable on the current record. Held for human review and follow-up; nothing was submitted to the payer.';
    } else {
      disposition = 'Held for human review. Nothing was submitted to the payer.';
    }
  } else {
    disposition = uhc.submission_disposition;
  }

  return (
    <Subsection idx="8" title="Submission & Audit">
      <div className="denials-summary-chips">
        <Chip label="Submission kind" value={submissionKind} />
        <Chip label="Auto-approved" value={String(autoApproved)}
          tone={autoApproved ? 'good' : 'warn'} />
        <Chip label="Filed automatically" value={String(filedAuto)} tone={filedAuto ? 'good' : 'warn'} />
        <Chip label="Payer status" value={sub.status_code != null ? String(sub.status_code) : null} />
        <Chip label="Saved" value={savedValue} />
      </div>
      {disposition && (
        <p className="denials-subsection__hint" style={{ marginTop: 8 }}>
          <strong>How this is handled:</strong> {disposition}
        </p>
      )}
      {autoDisabled && !humanReview && (
        <p className="denials-subsection__hint" style={{ marginTop: 4, color: '#92400e' }}>
          Autonomous payer submission is disabled. The packet is prepared and held for
          human review / the Claims module.
        </p>
      )}
      {humanReview && (
        <p className="denials-subsection__hint" style={{ marginTop: 4, color: '#92400e' }}>
          Human review required. The packet is held in pending_review and will not be
          submitted to the payer until a reviewer verifies authorization / retro-auth /
          exception pathway.
        </p>
      )}
      {resp && !humanReview && (
        <details style={{ marginTop: 8 }}>
          <summary>Payer response</summary>
          <pre className="denials-output-pre">{typeof resp === 'string' ? resp : JSON.stringify(resp, null, 2)}</pre>
        </details>
      )}
      {cited.length > 0 && (
        <>
          <h4 style={{ marginTop: 10 }}>Cited source documents</h4>
          <ul className="denials-doc-list">{cited.map((c, i) => <li key={i}>{String(c)}</li>)}</ul>
        </>
      )}
    </Subsection>
  );
}

// ---------------------------------------------------------------------------
// Top-level v2 view
// ---------------------------------------------------------------------------

function DenialResolveV2View({ nodeResults, contractRequest, onProvideInputs, submitting }) {
  const needsInput = Boolean(contractRequest?.needs_input);

  return (
    <section className="denials-block denials-block--analysis">
      <header className="denials-block__header">
        <h2>Denial Resolve v2</h2>
        <p>
          Deterministic engine + SME Resolution Cortex. Review the recommendation before sending.
        </p>
      </header>

      {needsInput && (
        <InputRequestForm request={contractRequest} onSubmit={onProvideInputs} submitting={submitting} />
      )}

      {!needsInput && (
        <>
          <DecisionSummaryBanner nodeResults={nodeResults} />
          <NotAppealableBanner nodeResults={nodeResults} />
          <CaseOverviewSection nodeResults={nodeResults} />
          <ResolutionSummarySection nodeResults={nodeResults} />
          <ClassificationDecisionSection nodeResults={nodeResults} />
          <CortexTacticsSection nodeResults={nodeResults} />
          <SpecialistFindingSection nodeResults={nodeResults} />
          <PlanSection nodeResults={nodeResults} />
          <FinancialSection nodeResults={nodeResults} />
          <AppealPacketSection nodeResults={nodeResults} />
          <ClinicalEvidenceSection nodeResults={nodeResults} />
          <SubmissionAuditSection nodeResults={nodeResults} />
        </>
      )}
    </section>
  );
}

export default DenialResolveV2View;
export { InputRequestForm };
